import React from 'react'

import { Question } from '@/types/question'
import { ZentakeContext } from '@/utils/ZentakeContext'

import athenaOptions, { conditionalAthenaOptionsValue } from './athena-options'
import chirotouchOptions, {
  conditionalChirotouchOptionsValue,
} from './chirotouch-options'
import elationOptions, {
  conditionalElationOptionsValue,
} from './elation-options'
import openDentalOptions, {
  conditionalOpenDentalOptionsValue,
} from './open-dental-options'

const useIntegrationOptions = (
  integration: 'athena' | 'chirotouch' | 'elation' | 'open_dental' | undefined
) => {
  switch (integration) {
    case 'athena':
      return athenaOptions
    case 'chirotouch':
      return chirotouchOptions
    case 'elation':
      return elationOptions
    case 'open_dental':
      return openDentalOptions
    default:
      return {}
  }
}

const conditionalIntegrationOptionsValue = (
  integration: 'athena' | 'chirotouch' | 'elation' | 'open_dental' | undefined,
  key: string,
  question: Question | undefined = undefined,
  includeFullName = false
): Array<{ label: string; value: string }> => {
  switch (integration) {
    case 'elation':
      return conditionalElationOptionsValue(question, key, includeFullName)
    case 'open_dental':
      return conditionalOpenDentalOptionsValue(question, key, includeFullName)
    case 'athena':
      return conditionalAthenaOptionsValue(question, key, includeFullName)
    case 'chirotouch':
      return conditionalChirotouchOptionsValue(question, key, includeFullName)
    default:
      return []
  }
}

const useIntegration = () => {
  const { teamDetails } = React.useContext(ZentakeContext)

  const integration = teamDetails?.integration?.integration_set
  const integrationReports = conditionalIntegrationOptionsValue(
    integration,
    'Reports'
  )
  const integrationOptions = useIntegrationOptions(integration)

  const memorizedIntegrationOptions = React.useMemo(() => {
    return integrationOptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(integrationOptions)])

  const getIntegrationOptionsValue: (
    key: string,
    question: Question | undefined,
    includeFullName?: boolean
  ) => Array<{ label: string; value: string }> = React.useCallback(
    (
      key: string,
      question: Question | undefined = undefined,
      includeFullName = false
    ) => {
      return conditionalIntegrationOptionsValue(
        integration,
        key,
        question,
        includeFullName
      )
    },
    [integration]
  )

  const integrationTitledName = React.useMemo(() => {
    if (integration === 'elation') {
      return 'Elation'
    }

    if (integration === 'open_dental') {
      return 'Open Dental'
    }

    if (integration === 'athena') {
      return 'Athena'
    }

    if (integration === 'chirotouch') {
      return 'Chirotouch'
    }

    return ''
  }, [integration])

  return {
    integration,
    integrationReports,
    integrationOptions: memorizedIntegrationOptions,
    getIntegrationOptionsValue,
    integrationTitledName,
  }
}

export default useIntegration
